import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  ArrowLeft,
  Check,
  CreditCard,
  FileText,
  Loader2,
  ShieldCheck,
  User,
  CalendarDays,
  IndianRupee,
} from "lucide-react";

import {
  getProductById,
  normalizeProduct,
  getCurrentUser,
  getCurrentUserType,
  createSubscriptionPurchase,
  confirmSubscriptionPayment,
  failSubscriptionPayment,
  formatDuration,
  planLimits,
  calculateSubscriptionAmount,
} from "../../services/subscriptionProductService";

import "./Subscription.css";

const STEPS = [
  { id: 1, label: "Review Plan", icon: FileText },
  { id: 2, label: "Billing Details", icon: User },
  { id: 3, label: "Payment", icon: CreditCard },
  { id: 4, label: "Success", icon: Check },
];

const initialBilling = {
  name: "",
  email: "",
  phone: "",
  company: "",
  gstNumber: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
};

const SubscriptionCheckout = () => {
  const navigate = useNavigate();
  const { planId } = useParams();

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [product, setProduct] = useState(null);
  const [step, setStep] = useState(1);

  const [billing, setBilling] = useState(initialBilling);

  const [paymentMethod, setPaymentMethod] = useState("ONLINE");
  const [transactionId, setTransactionId] = useState("");

  const [purchase, setPurchase] = useState(null);

  const [error, setError] = useState("");

  // =====================================================
  // LOAD PLAN
  // =====================================================

  useEffect(() => {
    loadPlan();
  }, [planId]);

  const loadPlan = async () => {
    try {
      setLoading(true);
      setError("");

      if (!planId) {
        throw new Error("Subscription plan ID is missing.");
      }

      const response = await getProductById(planId);
      const normalized = normalizeProduct(response);

      if (!normalized?.id) {
        throw new Error("Invalid subscription plan.");
      }

      if (!normalized.active) {
        throw new Error("This subscription plan is currently inactive.");
      }

      setProduct(normalized);

      // Load logged-in user details
      const user = getCurrentUser();

      if (user) {
        setBilling((prev) => ({
          ...prev,
          name:
            user.name ||
            user.fullName ||
            user.full_name ||
            user.username ||
            "",
          email: user.email || "",
          phone:
            user.phone ||
            user.mobile ||
            user.phoneNumber ||
            user.mobileNumber ||
            "",
          company:
            user.companyName ||
            user.company ||
            user.company_name ||
            "",
        }));
      }
    } catch (err) {
      console.error("Load subscription plan error:", err);
      setError(err.message || "Unable to load subscription plan.");
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // PRICE CALCULATION
  // =====================================================

  const priceDetails = useMemo(() => {
    if (!product) {
      return {
        price: 0,
        discount: 0,
        taxable: 0,
        gst: 0,
        total: 0,
      };
    }

    return calculateSubscriptionAmount(product.price, 0, 18);
  }, [product]);

  // =====================================================
  // LIMITS
  // =====================================================

  const limits = useMemo(() => {
    if (!product) return [];
    return planLimits(product);
  }, [product]);

  // =====================================================
  // BILLING CHANGE
  // =====================================================

  const handleBillingChange = (e) => {
    const { name, value } = e.target;

    setBilling((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =====================================================
  // VALIDATE BILLING
  // =====================================================

  const validateBilling = () => {
    if (!billing.name.trim()) {
      Swal.fire("Required", "Please enter your name.", "warning");
      return false;
    }

    if (!billing.email.trim()) {
      Swal.fire("Required", "Please enter your email.", "warning");
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billing.email)) {
      Swal.fire("Invalid Email", "Please enter a valid email address.", "warning");
      return false;
    }

    if (!billing.phone.trim()) {
      Swal.fire("Required", "Please enter your phone number.", "warning");
      return false;
    }

    if (!billing.address.trim()) {
      Swal.fire("Required", "Please enter your billing address.", "warning");
      return false;
    }

    if (!billing.city.trim()) {
      Swal.fire("Required", "Please enter your city.", "warning");
      return false;
    }

    if (!billing.state.trim()) {
      Swal.fire("Required", "Please enter your state.", "warning");
      return false;
    }

    if (!billing.pincode.trim()) {
      Swal.fire("Required", "Please enter your pincode.", "warning");
      return false;
    }

    return true;
  };

  // =====================================================
  // STEP 1 -> STEP 2
  // =====================================================

  const goToBilling = () => {
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // =====================================================
  // STEP 2 -> STEP 3
  // =====================================================

  const goToPayment = () => {
    if (!validateBilling()) return;

    setStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // =====================================================
  // CREATE PURCHASE
  // =====================================================

  const createPurchase = async () => {
    try {
      setProcessing(true);
      setError("");

      const user = getCurrentUser();

      if (!user?.id) {
        throw new Error("User session not found. Please login again.");
      }

      const userType = getCurrentUserType();

      if (!userType) {
        throw new Error("Unable to determine user type.");
      }

      const response = await createSubscriptionPurchase({
        productId: product.id,
        userId: user.id,
        userType,
        paymentMethod,
        discountAmount: 0,
      });

      setPurchase(response);

      return response;
    } catch (err) {
      console.error("Create purchase error:", err);

      Swal.fire({
        icon: "error",
        title: "Unable to Create Order",
        text: err.message || "Something went wrong.",
      });

      return null;
    } finally {
      setProcessing(false);
    }
  };

  // =====================================================
  // PAYMENT
  // =====================================================

  const handlePayment = async () => {
    if (!product) return;

    if (!paymentMethod) {
      Swal.fire(
        "Payment Method",
        "Please select a payment method.",
        "warning"
      );
      return;
    }

    try {
      setProcessing(true);
      setError("");

      let currentPurchase = purchase;

      // Create purchase only once
      if (!currentPurchase?.id) {
        currentPurchase = await createPurchase();

        if (!currentPurchase) {
          return;
        }

        setPurchase(currentPurchase);
      }

      /*
       * ---------------------------------------------------
       * DEMO PAYMENT FLOW
       * ---------------------------------------------------
       *
       * Current backend supports:
       *
       * POST /api/subscription-purchases/payment-success
       *
       * This is only for development/testing.
       *
       * Production should use Razorpay/other gateway with
       * server-side signature verification.
       */

      let txnId = transactionId.trim();

      if (!txnId) {
        txnId = `DEMO_TXN_${Date.now()}`;
        setTransactionId(txnId);
      }

      const successResponse = await confirmSubscriptionPayment({
        purchaseId: currentPurchase.id,
        transactionId: txnId,
        paymentMethod,
      });

      setPurchase(successResponse || currentPurchase);
      setStep(4);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      Swal.fire({
        icon: "success",
        title: "Payment Successful",
        text: "Your subscription has been activated.",
        confirmButtonColor: "#3E0F8D",
      });
    } catch (err) {
      console.error("Payment error:", err);

      try {
        if (purchase?.id) {
          await failSubscriptionPayment(purchase.id);
        }
      } catch (failureError) {
        console.error("Failed to mark purchase as failed:", failureError);
      }

      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text: err.message || "Unable to complete payment.",
      });
    } finally {
      setProcessing(false);
    }
  };

  // =====================================================
  // BACK
  // =====================================================

  const handleBack = () => {
    if (step === 1) {
      navigate(-1);
      return;
    }

    if (step === 2) {
      setStep(1);
      return;
    }

    if (step === 3) {
      setStep(2);
      return;
    }
  };

  // =====================================================
  // FORMAT CURRENCY
  // =====================================================

  const currency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div className="checkout-loading">
        <Loader2 className="checkout-spinner" size={38} />
        <p>Loading subscription plan...</p>
      </div>
    );
  }

  // =====================================================
  // ERROR
  // =====================================================

  if (error || !product) {
    return (
      <div className="checkout-error-page">
        <div className="checkout-error-card">
          <h2>Unable to Load Plan</h2>

          <p>
            {error ||
              "The requested subscription plan could not be found."}
          </p>

          <button
            className="checkout-primary-btn"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={18} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // =====================================================
  // SUCCESS
  // =====================================================

  if (step === 4) {
    return (
      <div className="checkout-page">
        <div className="checkout-container checkout-success-container">
          <div className="success-card">
            <div className="success-icon">
              <Check size={42} />
            </div>

            <h1>Subscription Activated!</h1>

            <p className="success-description">
              Your <strong>{product.name}</strong> subscription is now active.
            </p>

            <div className="success-details">
              <div>
                <span>Plan</span>
                <strong>{product.name}</strong>
              </div>

              <div>
                <span>Amount Paid</span>
                <strong>{currency(priceDetails.total)}</strong>
              </div>

              <div>
                <span>Transaction ID</span>
                <strong>{transactionId || "—"}</strong>
              </div>

              <div>
                <span>Validity</span>
                <strong>{formatDuration(product.duration)}</strong>
              </div>
            </div>

            <div className="success-actions">
              <button
                className="checkout-secondary-btn"
                onClick={() =>
                  navigate("/partner/my-subscription")
                }
              >
                View My Subscription
              </button>

              <button
                className="checkout-primary-btn"
                onClick={() =>
                  navigate("/partner/subscriptions")
                }
              >
                Browse Plans
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =====================================================
  // MAIN CHECKOUT
  // =====================================================

  return (
    <div className="checkout-page">
      <div className="checkout-container">

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="checkout-header">
          <button
            className="checkout-back-btn"
            onClick={handleBack}
          >
            <ArrowLeft size={18} />
            Back
          </button>

          <div>
            <h1>Complete Your Subscription</h1>
            <p>Secure checkout for your CertED subscription.</p>
          </div>
        </div>

        {/* =================================================
            STEPPER
        ================================================= */}

        <div className="checkout-stepper">
          {STEPS.map((item, index) => {
            const Icon = item.icon;
            const active = step === item.id;
            const completed = step > item.id;

            return (
              <React.Fragment key={item.id}>
                <div
                  className={`checkout-step ${
                    active ? "active" : ""
                  } ${completed ? "completed" : ""}`}
                >
                  <div className="step-circle">
                    {completed ? (
                      <Check size={17} />
                    ) : (
                      <Icon size={17} />
                    )}
                  </div>

                  <span>{item.label}</span>
                </div>

                {index < STEPS.length - 1 && (
                  <div
                    className={`step-line ${
                      step > item.id ? "completed" : ""
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* =================================================
            CONTENT
        ================================================= */}

        <div className="checkout-grid">

          {/* =================================================
              LEFT
          ================================================= */}

          <div className="checkout-main">

            {/* =================================================
                STEP 1
            ================================================= */}

            {step === 1 && (
              <div className="checkout-card">
                <div className="card-heading">
                  <div>
                    <span className="heading-label">
                      STEP 1
                    </span>
                    <h2>Review Your Plan</h2>
                  </div>
                </div>

                <div className="selected-plan">
                  <div className="selected-plan-top">
                    <div>
                      <span className="plan-badge">
                        {product.userType}
                      </span>

                      <h3>{product.name}</h3>

                      {product.code && (
                        <p className="plan-code">
                          {product.code}
                        </p>
                      )}
                    </div>

                    <div className="plan-price">
                      <strong>{currency(product.price)}</strong>
                      <span>
                        / {formatDuration(product.duration)}
                      </span>
                    </div>
                  </div>

                  {product.description && (
                    <p className="plan-description">
                      {product.description}
                    </p>
                  )}

                  <div className="plan-features">
                    <h4>Plan Includes</h4>

                    {limits.length > 0 ? (
                      <div className="limit-grid">
                        {limits.map((item) => (
                          <div
                            className="limit-item"
                            key={item.key}
                          >
                            <div className="limit-icon">
                              <Check size={15} />
                            </div>

                            <div>
                              <strong>
                                {item.value === "Unlimited"
                                  ? "Unlimited"
                                  : item.value}
                              </strong>

                              <span>{item.label}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-features">
                        Plan features are included in your
                        subscription.
                      </div>
                    )}
                  </div>
                </div>

                <div className="checkout-footer-actions">
                  <button
                    className="checkout-secondary-btn"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </button>

                  <button
                    className="checkout-primary-btn"
                    onClick={goToBilling}
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* =================================================
                STEP 2
            ================================================= */}

            {step === 2 && (
              <div className="checkout-card">
                <div className="card-heading">
                  <div>
                    <span className="heading-label">
                      STEP 2
                    </span>
                    <h2>Billing Details</h2>
                  </div>
                </div>

                <div className="form-grid">

                  <div className="form-group">
                    <label>
                      Full Name <span>*</span>
                    </label>

                    <input
                      type="text"
                      name="name"
                      value={billing.name}
                      onChange={handleBillingChange}
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Email Address <span>*</span>
                    </label>

                    <input
                      type="email"
                      name="email"
                      value={billing.email}
                      onChange={handleBillingChange}
                      placeholder="Enter email address"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Phone Number <span>*</span>
                    </label>

                    <input
                      type="tel"
                      name="phone"
                      value={billing.phone}
                      onChange={handleBillingChange}
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div className="form-group">
                    <label>Company Name</label>

                    <input
                      type="text"
                      name="company"
                      value={billing.company}
                      onChange={handleBillingChange}
                      placeholder="Enter company name"
                    />
                  </div>

                  <div className="form-group">
                    <label>GST Number</label>

                    <input
                      type="text"
                      name="gstNumber"
                      value={billing.gstNumber}
                      onChange={handleBillingChange}
                      placeholder="Optional"
                    />
                  </div>

                  <div className="form-group form-full">
                    <label>
                      Billing Address <span>*</span>
                    </label>

                    <textarea
                      name="address"
                      value={billing.address}
                      onChange={handleBillingChange}
                      placeholder="Enter complete billing address"
                      rows="3"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      City <span>*</span>
                    </label>

                    <input
                      type="text"
                      name="city"
                      value={billing.city}
                      onChange={handleBillingChange}
                      placeholder="City"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      State <span>*</span>
                    </label>

                    <input
                      type="text"
                      name="state"
                      value={billing.state}
                      onChange={handleBillingChange}
                      placeholder="State"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Pincode <span>*</span>
                    </label>

                    <input
                      type="text"
                      name="pincode"
                      value={billing.pincode}
                      onChange={handleBillingChange}
                      placeholder="Pincode"
                    />
                  </div>

                </div>

                <div className="checkout-footer-actions">
                  <button
                    className="checkout-secondary-btn"
                    onClick={() => setStep(1)}
                  >
                    <ArrowLeft size={17} />
                    Back
                  </button>

                  <button
                    className="checkout-primary-btn"
                    onClick={goToPayment}
                  >
                    Continue to Payment
                  </button>
                </div>
              </div>
            )}

            {/* =================================================
                STEP 3
            ================================================= */}

            {step === 3 && (
              <div className="checkout-card">

                <div className="card-heading">
                  <div>
                    <span className="heading-label">
                      STEP 3
                    </span>

                    <h2>Confirm Payment</h2>
                  </div>
                </div>

                <div className="payment-security">
                  <ShieldCheck size={22} />

                  <div>
                    <strong>Secure Checkout</strong>
                    <span>
                      Your subscription details are securely
                      processed.
                    </span>
                  </div>
                </div>

                <div className="payment-methods">

                  <label
                    className={`payment-method ${
                      paymentMethod === "ONLINE"
                        ? "selected"
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="ONLINE"
                      checked={paymentMethod === "ONLINE"}
                      onChange={(e) =>
                        setPaymentMethod(e.target.value)
                      }
                    />

                    <div className="payment-method-icon">
                      <CreditCard size={20} />
                    </div>

                    <div>
                      <strong>Online Payment</strong>
                      <span>
                        Credit Card / Debit Card / UPI /
                        Net Banking
                      </span>
                    </div>
                  </label>

                  <label
                    className={`payment-method ${
                      paymentMethod === "BANK_TRANSFER"
                        ? "selected"
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="BANK_TRANSFER"
                      checked={
                        paymentMethod === "BANK_TRANSFER"
                      }
                      onChange={(e) =>
                        setPaymentMethod(e.target.value)
                      }
                    />

                    <div className="payment-method-icon">
                      <IndianRupee size={20} />
                    </div>

                    <div>
                      <strong>Bank Transfer</strong>
                      <span>
                        Manual payment / NEFT / IMPS
                      </span>
                    </div>
                  </label>

                </div>

                <div className="transaction-box">
                  <label>
                    Transaction ID
                    <span className="optional">
                      Optional for demo
                    </span>
                  </label>

                  <input
                    type="text"
                    value={transactionId}
                    onChange={(e) =>
                      setTransactionId(e.target.value)
                    }
                    placeholder="Enter payment transaction ID"
                  />

                  <small>
                    For testing, if you leave this empty a
                    demo transaction ID will be generated.
                  </small>
                </div>

                <div className="billing-review">
                  <h4>Billing Information</h4>

                  <div className="review-row">
                    <span>Name</span>
                    <strong>{billing.name}</strong>
                  </div>

                  <div className="review-row">
                    <span>Email</span>
                    <strong>{billing.email}</strong>
                  </div>

                  <div className="review-row">
                    <span>Phone</span>
                    <strong>{billing.phone}</strong>
                  </div>

                  <div className="review-row">
                    <span>Address</span>
                    <strong>
                      {billing.address}, {billing.city},{" "}
                      {billing.state} - {billing.pincode}
                    </strong>
                  </div>
                </div>

                <div className="checkout-footer-actions">
                  <button
                    className="checkout-secondary-btn"
                    onClick={() => setStep(2)}
                    disabled={processing}
                  >
                    <ArrowLeft size={17} />
                    Back
                  </button>

                  <button
                    className="checkout-primary-btn payment-btn"
                    onClick={handlePayment}
                    disabled={processing}
                  >
                    {processing ? (
                      <>
                        <Loader2
                          size={18}
                          className="button-spinner"
                        />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard size={18} />
                        Pay {currency(priceDetails.total)}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

          </div>

          {/* =================================================
              RIGHT - ORDER SUMMARY
          ================================================= */}

          <aside className="checkout-sidebar">

            <div className="order-summary-card">

              <div className="summary-title">
                <h3>Order Summary</h3>
              </div>

              <div className="summary-plan">
                <div className="summary-plan-icon">
                  <CreditCard size={21} />
                </div>

                <div>
                  <strong>{product.name}</strong>
                  <span>
                    {formatDuration(product.duration)}
                  </span>
                </div>
              </div>

              <div className="summary-divider" />

              <div className="summary-row">
                <span>Plan Price</span>
                <strong>
                  {currency(priceDetails.price)}
                </strong>
              </div>

              <div className="summary-row">
                <span>Discount</span>
                <strong>
                  - {currency(priceDetails.discount)}
                </strong>
              </div>

              <div className="summary-row">
                <span>Taxable Amount</span>
                <strong>
                  {currency(priceDetails.taxable)}
                </strong>
              </div>

              <div className="summary-row">
                <span>GST (18%)</span>
                <strong>
                  {currency(priceDetails.gst)}
                </strong>
              </div>

              <div className="summary-divider" />

              <div className="summary-total">
                <span>Total Payable</span>
                <strong>
                  {currency(priceDetails.total)}
                </strong>
              </div>

              <div className="summary-security">
                <ShieldCheck size={16} />
                <span>Secure payment</span>
              </div>
            </div>

            <div className="plan-validity-card">

              <div className="validity-icon">
                <CalendarDays size={20} />
              </div>

              <div>
                <span>Subscription Validity</span>
                <strong>
                  {formatDuration(product.duration)}
                </strong>
              </div>

            </div>

            <div className="help-card">
              <h4>Need Help?</h4>

              <p>
                If you face any issue while purchasing a
                subscription, please contact CertED support.
              </p>
            </div>

          </aside>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCheckout;
